import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Walkthrough } from '@diwata/ironvale'
import { tourForPath } from '@/content/tours'
import './show-me.css'

// ShowMe — the "▸ Show me" trigger + guided Walkthrough, route-aware. Renders
// only where content/tours.ts defines a tour for the current page, so it never
// shows an empty affordance. Mounted once in Layout so every page inherits it
// without touching each page's header.
//
// First-visit nudge: the very first time a keeper lands on a toured page we
// offer a light, dismissible "New here? ▸ Show me" — reusing the onboarding
// dismiss pattern (a persisted per-page flag), so it never nags twice.

/** Per-page "already offered the tour" flag. Mirrors the onboardingDismissed idea. */
function seenKey(path: string): string {
  return `dw-tour-seen:${path}`
}
function hasSeen(path: string): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(seenKey(path)) === '1'
  } catch {
    return false
  }
}
function markSeen(path: string): void {
  try {
    localStorage.setItem(seenKey(path), '1')
  } catch {
    /* ignore private-mode storage errors */
  }
}

export function ShowMe() {
  const { pathname } = useLocation()
  const tour = tourForPath(pathname)
  const [open, setOpen] = useState(false)
  const [offer, setOffer] = useState(false)

  // On each route change, offer the tour once if this page has one and it hasn't
  // been seen. A short delay lets the page's real elements mount before we point.
  useEffect(() => {
    setOpen(false)
    setOffer(false)
    if (!tour) return
    if (hasSeen(tour.path)) return
    const id = setTimeout(() => setOffer(true), 600)
    return () => clearTimeout(id)
  }, [pathname, tour?.path])

  if (!tour) return null

  function launch() {
    markSeen(tour!.path)
    setOffer(false)
    setOpen(true)
  }
  function dismissOffer() {
    markSeen(tour!.path)
    setOffer(false)
  }

  return (
    <>
      {/* The persistent header trigger — always available where a tour exists. */}
      <button
        type="button"
        className="dw-showme"
        onClick={launch}
        aria-label={tour.label}
        title={tour.label}
      >
        <span className="dw-showme__play" aria-hidden="true">▸</span> Show me
      </button>

      {/* First-visit nudge — light, dismissible, appears next to the trigger. */}
      {offer && !open && (
        <div className="dw-showme-offer" role="note">
          <span className="dw-showme-offer__text">New here?</span>
          <button type="button" className="dw-showme-offer__go" onClick={launch}>
            ▸ Show me around
          </button>
          <button
            type="button"
            className="dw-showme-offer__x"
            aria-label="Dismiss"
            onClick={dismissOffer}
          >
            ×
          </button>
        </div>
      )}

      <Walkthrough steps={tour.steps} open={open} onClose={() => setOpen(false)} />
    </>
  )
}
